export type WidgetTabId = 'plan' | 'departures' | 'timetables';

export interface WidgetInitialConfig {
  initialTab: WidgetTabId;
  initialOrigin: string;
  initialDestination: string;
  departureStopId: string;
  disableMyLocation: boolean;
}

const DEFAULT_CONFIG: WidgetInitialConfig = {
  initialTab: 'plan',
  initialOrigin: '',
  initialDestination: '',
  departureStopId: '',
  disableMyLocation: false,
};

function readAttribute(element: HTMLElement, name: string): string | null {
  const value = element.getAttribute(name)?.trim();
  return value ? value : null;
}

function readBooleanAttribute(element: HTMLElement, name: string): boolean {
  const value = element.getAttribute(name);
  return value !== null && value !== 'false';
}

function readQueryParam(
  searchParams: URLSearchParams,
  key: string,
): string | null {
  const value = searchParams.get(`widget-${key}`)?.trim();
  return value ? value : null;
}

function normalizeTab(value: string | null): WidgetTabId {
  const normalized = value?.trim().toLowerCase();

  if (normalized === 'departures') {
    return 'departures';
  }

  if (normalized === 'timetables') {
    return 'timetables';
  }

  if (normalized === 'plan') {
    return 'plan';
  }

  return DEFAULT_CONFIG.initialTab;
}

/**
 * Resolves widget bootstrap config from element attributes and URL query params.
 * Element attributes take precedence over query params.
 */
export function resolveWidgetInitialConfig(
  element: HTMLElement,
  search = window.location.search,
): WidgetInitialConfig {
  const params = new URLSearchParams(search);

  const initialTab =
    readAttribute(element, 'initial-tab') ??
    readQueryParam(params, 'initial-tab');

  return {
    initialTab: normalizeTab(initialTab),
    initialOrigin:
      readAttribute(element, 'initial-origin') ??
      readQueryParam(params, 'initial-origin') ??
      DEFAULT_CONFIG.initialOrigin,
    initialDestination:
      readAttribute(element, 'initial-destination') ??
      readQueryParam(params, 'initial-destination') ??
      DEFAULT_CONFIG.initialDestination,
    departureStopId:
      readAttribute(element, 'departure-stop') ??
      readQueryParam(params, 'departure-stop') ??
      DEFAULT_CONFIG.departureStopId,
    disableMyLocation: readBooleanAttribute(element, 'disable-my-location'),
  };
}
